import type { User } from 'oidc-client-ts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { authApi } from '@/api/auth';
import { userManager } from '@/config/oidc';
import { i18n } from '@/locales';

let initialized = false;

export const useUserStore = defineStore('user', () => {
	// State
	const user = ref<User | null>(null);
	const loading = ref(false);
	const error = ref<string | null>(null);
	const dbFirstName = ref<string | null>(null);
	const dbLastName = ref<string | null>(null);

	// Computed
	const isAuthenticated = computed(() => !!user.value && !user.value.expired);
	const accessToken = computed(() => user.value?.access_token ?? null);
	const displayName = computed(() => {
		if (dbFirstName.value !== null) {
			return [dbFirstName.value, dbLastName.value].filter(Boolean).join(' ').trim() || null;
		}
		return user.value?.profile?.name ?? user.value?.profile?.preferred_username ?? null;
	});

	// Actions

	async function fetchDbProfile(): Promise<void> {
		if (!user.value || user.value.expired) return;
		try {
			const profile = await authApi.getMe();
			if ('id' in profile) {
				dbFirstName.value = profile.firstName;
				dbLastName.value = profile.lastName;
			}
		} catch {
			// OIDC name is the fallback — ignore API errors here
		}
	}

	/**
	 * Loads user from storage and subscribes to OIDC events
	 */
	async function initialize(): Promise<void> {
		if (initialized) return;
		initialized = true;

		if (!userManager) {
			console.warn('[OIDC] userManager is not configured — skipping initialization');
			return;
		}

		loading.value = true;
		error.value = null;

		try {
			user.value = await userManager.getUser();
			await fetchDbProfile();
		} catch (err) {
			error.value = 'Failed to load user session';
			console.error('[OIDC] getUser failed:', err);
		} finally {
			loading.value = false;
		}

		userManager.events.addUserLoaded(async loadedUser => {
			const previousSub = user.value?.profile?.sub;
			user.value = loadedUser;
			if (loadedUser.profile?.sub !== previousSub) {
				dbFirstName.value = null;
				dbLastName.value = null;
				await fetchDbProfile();
			}
		});

		userManager.events.addUserUnloaded(() => {
			user.value = null;
			dbFirstName.value = null;
			dbLastName.value = null;
		});

		userManager.events.addUserSignedOut(() => {
			user.value = null;
			dbFirstName.value = null;
			dbLastName.value = null;
		});

		userManager.events.addSilentRenewError(err => {
			error.value = err.message;
		});
	}

	/**
	 * Redirects to Keycloak login page
	 *
	 * @param returnUrl - URL to return to after login
	 */
	async function login(returnUrl?: string): Promise<void> {
		if (!userManager) return;
		const uiLocales = i18n.global.locale.value;
		await userManager.signinRedirect({
			state: { returnUrl: returnUrl ?? '/' },
			extraQueryParams: { ui_locales: uiLocales }
		});
	}

	/**
	 * Redirects to Keycloak logout page
	 */
	async function logout(): Promise<void> {
		if (!userManager) return;
		await userManager.signoutRedirect();
	}

	/**
	 * Handles the OIDC redirect callback after login
	 *
	 * @returns The authenticated User object
	 */
	async function handleCallback(): Promise<User> {
		if (!userManager) throw new Error('OIDC not configured');
		const callbackUser = await userManager.signinRedirectCallback();
		user.value = callbackUser;
		return callbackUser;
	}

	function updateProfile(firstName: string, lastName: string): void {
		dbFirstName.value = firstName;
		dbLastName.value = lastName;
	}

	return {
		// State
		user,
		loading,
		error,
		dbFirstName,
		dbLastName,

		// Computed
		isAuthenticated,
		accessToken,
		displayName,

		// Actions
		initialize,
		login,
		logout,
		handleCallback,
		updateProfile
	};
});
